import React, { useRef, useEffect, useCallback } from 'react';
import { sanitizeHtml } from '../builder/FormattedTextarea';

/**
 * InlineEditable — contentEditable text component for preview inline editing.
 *
 * When `isEditing` is true, renders as a contentEditable div.
 * When false, renders children normally (pass-through).
 *
 * Handles the React + contentEditable cursor-reset problem by:
 *   - Setting innerHTML ONLY when entering edit mode
 *   - Never updating innerHTML from props while editing
 *   - Reading innerHTML on input/blur to sync back to state
 *
 * @param {Object} props
 * @param {string}   props.value      — HTML string (e.g., data.title)
 * @param {boolean}  props.isEditing  — whether this element is in edit mode
 * @param {function} props.onChange    — (newHtml: string) => void — on each input
 * @param {function} props.onBlur     — () => void — when editing ends (blur / ESC)
 * @param {React.ReactNode} props.children — normal render content (when not editing)
 * @param {string}   props.className
 * @param {Object}   props.style
 * @param {function} props.onClick    — click handler (pass-through for non-editing mode)
 * @param {boolean}  props.plainText  — if true, use textContent instead of innerHTML (for non-HTML fields)
 */
export function InlineEditable({
  value,
  isEditing,
  onChange,
  onBlur,
  children,
  className,
  style,
  onClick,
  plainText = false,
}) {
  const ref = useRef(null);
  const wasEditingRef = useRef(false);

  // Enter / exit edit mode
  useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      // ── Entering edit mode ──
      wasEditingRef.current = true;
      const el = ref.current;
      if (el) {
        if (plainText) {
          el.textContent = value || '';
        } else {
          el.innerHTML = value || '';
        }
        el.focus();
        // Place cursor at end of content
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } else if (!isEditing && wasEditingRef.current) {
      // ── Exiting edit mode ──
      wasEditingRef.current = false;
    }
    // Intentionally no `value` dep — avoid resetting innerHTML while editing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const handleInput = useCallback(() => {
    if (ref.current && onChange) {
      if (plainText) {
        onChange(ref.current.textContent);
      } else {
        // Sanitize HTML the same way FormattedTextarea does —
        // converts <div>/<p> to <br>, strips unknown tags.
        // Prevents duplicate paragraphs from browser DOM restructuring.
        onChange(sanitizeHtml(ref.current.innerHTML));
      }
    }
  }, [onChange, plainText]);

  const handleBlur = useCallback(() => {
    if (onBlur) onBlur();
  }, [onBlur]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      ref.current?.blur();
    }
    // Prevent ArrowLeft/Right from changing slides while editing
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.stopPropagation();
    }
  }, []);

  // ── Editing mode — contentEditable ──
  // Key="inline-editing" forces React to mount a fresh DOM element,
  // preventing innerHTML carryover from the previous mode.
  if (isEditing) {
    return (
      <div
        key="inline-editing"
        ref={ref}
        className={className}
        style={{ ...style, outline: 'none', cursor: 'text' }}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  // ── Normal mode — render children with click handler ──
  // Key="inline-display" ensures a clean DOM node (no leftover innerHTML).
  return (
    <div key="inline-display" className={className} style={style} onClick={onClick}>
      {children}
    </div>
  );
}
